import { lazy, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, type RouteObject } from 'react-router';
import { AppShell } from './components/AppShell';
import { useSession } from './lib/SignaSessionContext';
import LoginPage from './pages/LoginPage';
import CallbackPage from './pages/CallbackPage';

// El mapa de rutas.
//
// RUTAS EN INGLÉS, SIN EXCEPCIONES (policies/architecture.md §3): `/inbox`, no `/bandeja`. Las URLs
// son identificadores técnicos y siguen la misma regla que las tablas y los parámetros de la API;
// las etiquetas visibles van en español rioplatense. Nexa no tiene páginas públicas anónimas, así
// que la regla no tiene el caso borde de una landing.
//
// CARGA DIFERIDA POR RUTA: el constructor y el workspace terminan en bundles distintos. No es una
// optimización cosmética — el constructor es la herramienta de un puñado de analistas y el
// workspace lo abre toda la organización, muchas veces desde un teléfono. Que quien pide una
// licencia descargue el diseñador de flujos es cobrarle a la mayoría el costo de la minoría.

const InboxPage = lazy(() => import('./pages/InboxPage'));
const TaskDetailPage = lazy(() => import('./pages/TaskDetailPage'));
const CasesPage = lazy(() => import('./pages/CasesPage'));
const CaseDetailPage = lazy(() => import('./pages/CaseDetailPage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const FormListPage = lazy(() => import('./pages/builder/FormListPage'));
const FormBuilderPage = lazy(() => import('./pages/builder/FormBuilderPage'));
const FlowListPage = lazy(() => import('./pages/builder/FlowListPage'));
const FlowBuilderPage = lazy(() => import('./pages/builder/FlowBuilderPage'));
// La migración de casos vivos es una RUTA APARTE, no una pestaña del editor: mover casos en curso
// a otra versión es una operación explícita y auditada, jamás un efecto secundario de guardar.
const FlowMigrationPage = lazy(() => import('./pages/builder/FlowMigrationPage'));

/**
 * Guard de sesión. Sin sesión: a `/login`, recordando a dónde iba — volver siempre a la bandeja
 * después de entrar convierte cada enlace compartido a una tarea en un viaje de tres pasos.
 *
 * ESTO NO ES UN CONTROL DE SEGURIDAD: es navegación. Quien autoriza es `nexa-api` con el token de
 * Signa en cada pedido. Un guard de front que "protege" datos protege exactamente nada.
 */
export function RequireSession({ children }: { children?: ReactNode }) {
  const { isAuthenticated } = useSession();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children ? <>{children}</> : <Outlet />;
}

export const ROUTES: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/auth/callback', element: <CallbackPage /> },
  {
    element: <RequireSession />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/', element: <Navigate to="/inbox" replace /> },
          // ── Workspace ──────────────────────────────────────────────────────────────────────
          { path: '/inbox', element: <InboxPage /> },
          { path: '/tasks/:id', element: <TaskDetailPage /> },
          { path: '/cases', element: <CasesPage /> },
          { path: '/cases/:id', element: <CaseDetailPage /> },
          // ── Constructor ────────────────────────────────────────────────────────────────────
          { path: '/builder/forms', element: <FormListPage /> },
          { path: '/builder/forms/:key', element: <FormBuilderPage /> },
          { path: '/builder/flows', element: <FlowListPage /> },
          { path: '/builder/flows/:key', element: <FlowBuilderPage /> },
          { path: '/builder/flows/:key/migration', element: <FlowMigrationPage /> },
          // ── Administración ─────────────────────────────────────────────────────────────────
          { path: '/admin/settings', element: <AdminSettingsPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
